import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';

import { CommonModule } from '@angular/common';

import {
  DomSanitizer,
  SafeResourceUrl
} from '@angular/platform-browser';

import { FormsModule } from '@angular/forms';

import { SiteSettingsService } from '../../../../services/site-setting.service';
import { ToastService } from '../../../../services/toast.service';


@Component({
  selector: 'app-admin-youtube',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule
  ],
  templateUrl: './admin-youtube.component.html',
  styleUrls: [
    '../../admin-section.shared.css',
    './admin-youtube.component.css'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminYoutubeComponent implements OnInit {

  youtubeInput = '';
  youtubeVideoId = '';
  youtubeError = '';

  youtubePreviewUrl: SafeResourceUrl | null = null;


  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly siteSettingService: SiteSettingsService,
    private readonly toastService: ToastService
  ) {}


  ngOnInit(): void {
    this.loadYouTube();
  }


  loadYouTube(): void {

    this.siteSettingService
      .getYouTube()
      .subscribe({

        next: response => {

          this.youtubeInput =
            response.youtubeUrl ?? '';

          if (this.youtubeInput) {
            this.previewYouTube();
          } else {
            this.youtubeVideoId = '';
            this.youtubePreviewUrl = null;
            this.youtubeError = '';
          }

          this.changeDetector.markForCheck();
        },

        error: error => {

          console.error(
            'Unable to load YouTube video:',
            error
          );

          this.toastService.error(
            'Unable to load featured YouTube video.'
          );
        }

      });
  }


  previewYouTube(): void {

    const id =
      this.extractYouTubeId(this.youtubeInput);

    if (!id) {

      this.youtubeVideoId = '';
      this.youtubePreviewUrl = null;

      this.youtubeError =
        'Enter a valid YouTube watch, share, Shorts or embed link.';

      return;
    }

    this.youtubeError = '';
    this.youtubeVideoId = id;

    this.youtubePreviewUrl =
      this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://www.youtube-nocookie.com/embed/${id}`
      );
  }


  saveYouTube(): void {

    this.previewYouTube();

    if (!this.youtubeVideoId) {

      this.toastService.error(
        'Enter a valid YouTube link.'
      );

      return;
    }

    this.siteSettingService
      .updateYouTube(this.youtubeInput.trim())
      .subscribe({

        next: response => {

          this.toastService.success(
            response.message
          );
        },

        error: error => {

          console.error(
            'Unable to save YouTube video:',
            error
          );

          this.toastService.error(
            error.error?.message ??
              'Unable to save YouTube video.'
          );
        }

      });
  }


  private extractYouTubeId(
    value: string
  ): string | null {

    const input = value.trim();

    if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
      return input;
    }

    try {

      const url = new URL(input);

      const host =
        url.hostname.replace(/^www\./, '');

      let id = '';

      if (host === 'youtu.be') {

        id =
          url.pathname
            .split('/')
            .filter(Boolean)[0] ?? '';

      } else if (
        host === 'youtube.com' ||
        host === 'm.youtube.com' ||
        host === 'youtube-nocookie.com'
      ) {

        if (url.pathname === '/watch') {
          id =
            url.searchParams.get('v') ?? '';
        }

        if (
          url.pathname.startsWith('/shorts/')
        ) {
          id =
            url.pathname.split('/')[2] ?? '';
        }

        if (
          url.pathname.startsWith('/embed/')
        ) {
          id =
            url.pathname.split('/')[2] ?? '';
        }
      }

      return /^[a-zA-Z0-9_-]{11}$/.test(id)
        ? id
        : null;

    } catch {
      return null;
    }
  }

}